import { groupBy } from 'lodash-es';
import { useMemo, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import {
  Content,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, DividerLine, Stack } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Listing,
  SectionInfo,
  SectionSectionGroup,
} from 'src/WebApiController';

import * as styles from './ModifySectionGroup.css';
import {
  toDisplayName,
  toListingStats,
  toSectionGroupKey,
} from './SectionGroupPanelV2.utils';

export type ZoneGroup = {
  zone: string;
  area: string;
  color?: string;
};

export const SectionStatLabel = ({
  titleContentId,
  stat,
}: {
  titleContentId: ContentId | string;
  stat: string;
}) => {
  return (
    <Stack
      direction="column"
      alignItems="start"
      justifyContent="center"
      gap="s"
      className={styles.statLabel}
    >
      <span className={styles.statStyle}>{stat}</span>
      <span className={styles.titleStyle}>
        <Content id={titleContentId} />
      </span>
    </Stack>
  );
};

type ModifySectionGroupProps = {
  sectionGroup: SectionSectionGroup;
  selectedSections: SectionInfo[];
  existingGroupIds: string[];
  onSaveSectionGroup: (sectionGroup: SectionSectionGroup) => void;
  onDeleteSectionGroup?: () => void;
};

export const ModifySectionGroup = ({
  sectionGroup,
  selectedSections,
  existingGroupIds,
  onSaveSectionGroup,
  onDeleteSectionGroup,
}: ModifySectionGroupProps) => {
  // Ideally this should be access in the parent
  // but this is to avoid a rerender of the modal upon catalog listings refresh
  const { eventsTransformed } = useCatalogDataContext();
  const sectionListings = useMemo(() => {
    const listings = (eventsTransformed ?? [])
      .flatMap((event) => event.entities.listings ?? [])
      .flatMap(flattenListingGroup)
      .map((l) => l as Listing)
      .filter((l) => !l.isLtGrp && l.id > 0 && Boolean(l.seating?.sectionId));
    return groupBy(listings, (l) => l.seating!.sectionId);
  }, [eventsTransformed]);
  const selectedListings = useMemo(
    () => selectedSections.flatMap(({ id }) => sectionListings[id] ?? []),
    [sectionListings, selectedSections]
  );

  const contentContext = useContentContext();

  const [localSectionGroup, setLocalSectionGroup] =
    useState<SectionSectionGroup>(sectionGroup);

  const soldText = useContent(ContentId.Sold);
  const unsoldText = useContent(ContentId.Unsold);
  const addZoneNameText = useContent(ContentId.AddName);

  const isGeneratedZoneGroup = useMemo(() => {
    return (
      sectionGroup.ticketClassId != null &&
      sectionGroup.sectionGroupAreaType != null
    );
  }, [sectionGroup.sectionGroupAreaType, sectionGroup.ticketClassId]);

  const isDuplicate = useMemo(
    () =>
      existingGroupIds
        .filter((id) => id !== toSectionGroupKey(sectionGroup))
        .includes(toSectionGroupKey(localSectionGroup)),
    [existingGroupIds, localSectionGroup, sectionGroup]
  );

  const readyToSave = useMemo(() => {
    if (isGeneratedZoneGroup) {
      return Boolean(localSectionGroup.sectionGroupColor);
    }
    return Boolean(localSectionGroup.sectionGroupAreaName);
  }, [
    isGeneratedZoneGroup,
    localSectionGroup.sectionGroupAreaName,
    localSectionGroup.sectionGroupColor,
  ]);

  const listingStats = useMemo(
    () => toListingStats(selectedListings),
    [selectedListings]
  );

  return (
    <Stack
      direction="column"
      width="full"
      gap="m"
      style={{ padding: `${vars.spacing.m} 0`, color: vars.color.textPrimary }}
    >
      <Stack width="full" gap="m" style={{ padding: vars.spacing.m }}>
        <PosTextField
          type="text"
          value={toDisplayName(localSectionGroup, contentContext) ?? ''}
          disabled={isGeneratedZoneGroup}
          placeholder={addZoneNameText}
          onChange={(e) => {
            setLocalSectionGroup((prev) => {
              return {
                ...prev,
                sectionGroupAreaName: e.target.value,
              };
            });
          }}
        />
      </Stack>
      <Stack width="full" className={styles.colorPicker}>
        <HexColorPicker
          color={localSectionGroup.sectionGroupColor}
          onChange={(updated: string) => {
            setLocalSectionGroup((prev) => ({
              ...prev,
              sectionGroupColor: updated.toUpperCase(),
            }));
          }}
        />
      </Stack>
      <div className={styles.statsContainer}>
        <SectionStatLabel
          stat={listingStats.sectionCount}
          titleContentId={ContentId.Sections}
        />
        <DividerLine vertical height={'100%'} />
        <SectionStatLabel
          stat={listingStats.listingCount}
          titleContentId={ContentId.Listings}
        />
        <DividerLine vertical height={'100%'} />
        <SectionStatLabel
          stat={listingStats.soldUnsold}
          titleContentId={`${soldText}/${unsoldText}`}
        />
        <DividerLine vertical height={'100%'} />
        <SectionStatLabel
          stat={listingStats.listedCount}
          titleContentId={ContentId.Broadcast}
        />
      </div>

      <Stack justifyContent="spaceBetween" alignItems="center" width="full">
        <div>
          {onDeleteSectionGroup && (
            <Button
              variant="textPlain"
              onClick={onDeleteSectionGroup}
              style={{ color: vars.color.textError }}
            >
              <Content id={ContentId.DeleteGroup} />
            </Button>
          )}
        </div>
        <Button
          disabled={!readyToSave || isDuplicate}
          onClick={() => onSaveSectionGroup(localSectionGroup)}
        >
          <Content id={ContentId.Save} />
        </Button>
      </Stack>
    </Stack>
  );
};
